import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Field, Input, Message, Modal } from 'ui'
import { useCustomFieldsContext } from '../context/CustomFieldsProvider'

const KEY_MAX_LENGTH = 50

const schema = z.object({
  key: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(KEY_MAX_LENGTH, `Name must be at most ${KEY_MAX_LENGTH} characters`),
})

type FormValues = z.infer<typeof schema>

export const CustomFieldFormModal = () => {
  const {
    state: { editingField, isSaving, saveErrorMessage },
    actions: { closeForm, saveField },
  } = useCustomFieldsContext()

  const isEditing = !!editingField
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { key: editingField?.key ?? '' },
  })

  useEffect(() => {
    reset({ key: editingField?.key ?? '' })
  }, [editingField, reset])

  const onSubmit = handleSubmit((values) => {
    saveField(values.key)
  })

  return (
    <Modal
      title={isEditing ? 'Edit tag' : 'Add tag'}
      open
      onClose={closeForm}
      showCancel
      onOk={onSubmit}
      okText={isEditing ? 'Save' : 'Add'}
      loading={isSaving}
    >
      <Field label="Name" error={errors.key?.message}>
        <Controller
          name="key"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              autoFocus
              placeholder="e.g. Event"
              maxLength={KEY_MAX_LENGTH}
            />
          )}
        />
      </Field>
      {saveErrorMessage ? (
        <Message variant="error" mt={3}>
          {saveErrorMessage}
        </Message>
      ) : null}
    </Modal>
  )
}
